import { useState, useRef, useEffect } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus, Trash2, ShoppingCart, FileText, ChevronRight,
  Save, X, Edit2, Copy, AlertCircle, CheckCircle, Download, Printer, Search, Package
} from 'lucide-react'
import { templateService, productService } from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useCartStore } from '../store/cartStore'
import { PageSpinner } from '../components/ui'
import toast from 'react-hot-toast'

// ── Helpers ────────────────────────────────────────────────────────
const fmtPrice = (p) => p != null ? `R ${parseFloat(p).toFixed(2)}` : '—'
const fmtDate  = (d) => new Date(d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })

// Empty row for the editor grid
const emptyRow = () => ({ stock_code: '', description: '', quantity: 1, note: '' })

// ── Main Page ──────────────────────────────────────────────────────
export default function Templates() {
  const { isAuthenticated, loading } = useAuth()
  const qc = useQueryClient()

  const [view, setView]           = useState('list')        // 'list' | 'edit'
  const [activeId, setActiveId]   = useState(null)          // template being edited
  const [creating, setCreating]   = useState(false)         // new-template name dialog
  const [newName, setNewName]     = useState('')
  const [newDesc, setNewDesc]     = useState('')

  const { data: templates, isLoading } = useQuery({
    queryKey: ['templates'],
    queryFn:  templateService.getAll,
    enabled:  isAuthenticated,
  })

  const { data: activeTemplate, isLoading: loadingTemplate } = useQuery({
    queryKey: ['template', activeId],
    queryFn:  () => templateService.getById(activeId),
    enabled:  !!activeId,
  })

  const createMut = useMutation({
    mutationFn: templateService.create,
    onSuccess: (tmpl) => {
      qc.invalidateQueries(['templates'])
      setCreating(false)
      setNewName('')
      setNewDesc('')
      setActiveId(tmpl.id)
      setView('edit')
      toast.success('Template created')
    },
    onError: () => toast.error('Failed to create template'),
  })

  const deleteMut = useMutation({
    mutationFn: templateService.delete,
    onSuccess: () => { qc.invalidateQueries(['templates']); toast.success('Template deleted') },
    onError:   () => toast.error('Failed to delete template'),
  })

  if (loading)         return <PageSpinner />
  if (!isAuthenticated) return <Navigate to="/login" replace />

  const list = Array.isArray(templates) ? templates : []

  // ── List view ──────────────────────────────────────────────────
  if (view === 'list') return (
    <div className="max-w-4xl mx-auto px-4 py-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link to="/account" className="text-gray-400 hover:text-gray-600">
            <ChevronRight size={18} className="rotate-180" />
          </Link>
          <h1 className="section-title">Order Templates</h1>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="btn-primary flex items-center gap-2 text-sm"
        >
          <Plus size={16} /> New Template
        </button>
      </div>

      {/* New template dialog */}
      {creating && (
        <div className="card p-5 mb-6 border-2 border-primary/30 animate-slide-up">
          <h3 className="font-display font-semibold text-gray-800 dark:text-white mb-4">New Template</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Template Name *</label>
              <input
                className="input-field"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="e.g. Weekly Beer Order, Monthly Spirits Run…"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Description (optional)</label>
              <input
                className="input-field"
                value={newDesc}
                onChange={e => setNewDesc(e.target.value)}
                placeholder="Short description…"
              />
            </div>
            <div className="flex gap-3 pt-1">
              <button
                onClick={() => createMut.mutate({ name: newName, description: newDesc, items: [] })}
                disabled={!newName.trim() || createMut.isLoading}
                className="btn-primary flex items-center gap-2"
              >
                <Save size={14} /> {createMut.isLoading ? 'Creating…' : 'Create & Add Lines'}
              </button>
              <button onClick={() => { setCreating(false); setNewName(''); setNewDesc('') }} className="btn-outline flex items-center gap-2">
                <X size={14} /> Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template list */}
      {isLoading ? <PageSpinner /> : list.length === 0 ? (
        <div className="text-center py-16">
          <div className="text-6xl mb-4">📋</div>
          <h3 className="font-heading text-xl mb-2 text-gray-800 dark:text-white">No templates yet</h3>
          <p className="text-gray-500 text-sm mb-6">
            Save recurring orders as templates to re-order with one click.
          </p>
          <button onClick={() => setCreating(true)} className="btn-primary inline-flex items-center gap-2">
            <Plus size={16} /> Create your first template
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {list.map(t => (
            <TemplateCard
              key={t.id}
              template={t}
              onEdit={() => { setActiveId(t.id); setView('edit') }}
              onDelete={() => { if (confirm(`Delete "${t.name}"?`)) deleteMut.mutate(t.id) }}
            />
          ))}
        </div>
      )}
    </div>
  )

  // ── Edit view ──────────────────────────────────────────────────
  if (view === 'edit') return (
    <TemplateEditor
      template={activeTemplate}
      loading={loadingTemplate}
      onBack={() => { setView('list'); setActiveId(null); qc.invalidateQueries(['templates']) }}
    />
  )
}

// ── Template Card (list) ───────────────────────────────────────────
function TemplateCard({ template: t, onEdit, onDelete }) {
  const qc = useQueryClient()
  const syncFromAPI = useCartStore(s => s.syncFromAPI)
  const zeroAllQuantities = useCartStore(s => s.zeroAllQuantities)

  const loadMut = useMutation({
    mutationFn: () => templateService.loadToCart(t.id),
    onSuccess:  async () => {
      await syncFromAPI()
      await zeroAllQuantities()
      qc.invalidateQueries(['cart'])
      toast.success(`"${t.name}" loaded — enter your quantities`)
    },
    onError:    () => toast.error('Failed to load template'),
  })

  return (
    <div className="card p-5 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <FileText size={15} className="text-primary flex-shrink-0" />
          <h3 className="font-display font-semibold text-gray-900 dark:text-white truncate">{t.name}</h3>
        </div>
        {t.description && (
          <p className="text-xs text-gray-400 mb-1">{t.description}</p>
        )}
        <p className="text-xs text-gray-400">
          {t.item_count} line{t.item_count !== 1 ? 's' : ''} · Updated {fmtDate(t.updated_at || t.created_at)}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={() => loadMut.mutate()}
          disabled={loadMut.isLoading || t.item_count === 0}
          title="Load into cart"
          className="btn-primary flex items-center gap-1.5 text-xs py-2 px-3 disabled:opacity-50"
        >
          <ShoppingCart size={13} />
          {loadMut.isLoading ? 'Loading…' : 'Load to Cart'}
        </button>
        <button onClick={onEdit} title="Edit template" className="btn-outline p-2">
          <Edit2 size={14} />
        </button>
        <button onClick={onDelete} title="Delete template" className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  )
}

// ── Template Editor ────────────────────────────────────────────────
function TemplateEditor({ template, loading, onBack }) {
  const qc = useQueryClient()
  const [rows, setRows]         = useState(null)   // null = not yet initialised
  const [name, setName]         = useState('')
  const [desc, setDesc]         = useState('')
  const [dirty, setDirty]       = useState(false)
  const [saving, setSaving]     = useState(false)
  const [search, setSearch]     = useState('')
  const [searchRes, setSearchRes] = useState([])
  const [searching, setSearching] = useState(false)
  const searchRef               = useRef(null)

  // Initialise from loaded template (once)
  if (template && rows === null) {
    setRows(template.items.length > 0
      ? template.items.map(i => ({ stock_code: i.stock_code, description: i.description || '', quantity: i.quantity, note: i.note || '' }))
      : [emptyRow()]
    )
    setName(template.name)
    setDesc(template.description || '')
  }

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchRes([])
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleProductSearch = async (q) => {
    setSearch(q)
    if (q.length < 2) { setSearchRes([]); return }
    setSearching(true)
    try {
      const data = await productService.search(q, { limit: 10 })
      setSearchRes(Array.isArray(data) ? data : (data?.items ?? []))
    } catch { setSearchRes([]) }
    finally { setSearching(false) }
  }

  const addProduct = (product) => {
    const desc     = product.description || product.stock_code || ''
    const emptyIdx = (rows || []).findIndex(r => !(r.description || r.stock_code || '').trim())
    const exists   = (rows || []).find(r => r.stock_code === product.stock_code || r.description === desc)
    if (exists) {
      setRows(prev => prev.map(r =>
        (r.stock_code === product.stock_code || r.description === desc)
          ? { ...r, quantity: r.quantity + 1 }
          : r
      ))
    } else if (emptyIdx >= 0) {
      setRows(prev => prev.map((r, i) => i === emptyIdx
        ? { stock_code: product.stock_code, description: desc, quantity: 1, note: '' }
        : r
      ))
    } else {
      setRows(prev => [...prev, { stock_code: product.stock_code, description: desc, quantity: 1, note: '' }])
    }
    setSearch(''); setSearchRes([]); setDirty(true)
    toast.success(`Added: ${product.description || product.stock_code}`, { duration: 1500 })
  }

  const updateRow = (idx, field, value) => {
    setRows(r => r.map((row, i) => i === idx ? { ...row, [field]: value } : row))
    setDirty(true)
  }
  const addRow = () => { setRows(r => [...r, emptyRow()]); setDirty(true) }
  const removeRow = (idx) => { setRows(r => r.filter((_, i) => i !== idx)); setDirty(true) }

  const handleSave = async () => {
    setSaving(true)
    try {
      const validRows = rows.filter(r => (r.description || r.stock_code || '').trim())
      await templateService.update(template.id, { name, description: desc })
      await templateService.saveItems(template.id, validRows.map((r, i) => ({
        stock_code: r.stock_code ? r.stock_code.toUpperCase().trim() : (r.description || '').trim().substring(0, 50),
        description: r.description || r.stock_code || '',
        quantity:   Math.max(1, parseInt(r.quantity) || 1),
        note:       r.note || null,
        sort_order: i,
      })))
      qc.invalidateQueries(['template', template.id])
      setDirty(false)
      toast.success('Template saved')
    } catch {
      toast.error('Failed to save template')
    } finally {
      setSaving(false)
    }
  }

  const loadMut = useMutation({
    mutationFn: () => templateService.loadToCart(template.id),
    onSuccess:  async () => {
      await useCartStore.getState().syncFromAPI()
      await useCartStore.getState().zeroAllQuantities()
      toast.success('Template loaded — enter your quantities')
    },
    onError:    () => toast.error('Failed to load template'),
  })

  const handleExportCSV = () => {
    const validRows = rows.filter(r => (r.description || r.stock_code || '').trim())
    if (!validRows.length) return
    const lines = ['description,quantity,note']
    validRows.forEach(r => {
      const desc_val = (r.description || r.stock_code || '').replace(/"/g, '""')
      const note = (r.note || '').replace(/"/g, '""')
      lines.push(`"${desc_val}",${r.quantity},"${note}"`)
    })
    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.download = `${name.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'template'}.csv`
    a.click()
    URL.revokeObjectURL(url)
    toast.success('CSV downloaded')
  }

  const handlePrint = () => {
    const validRows = rows.filter(r => (r.description || r.stock_code || '').trim())
    if (!validRows.length) return
    const dateStr = new Date().toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })
    const rowsHtml = validRows.map((r, i) => `
      <tr style="background:${i % 2 === 0 ? '#fff' : '#f9f9f9'}">
        <td style="padding:7px 10px;border-bottom:1px solid #eee;color:#333">${r.description || r.stock_code || ''}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #eee;text-align:center">${r.quantity}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #eee;color:#555">${r.note || ''}</td>
      </tr>`).join('')
    const logoUrl = `${window.location.origin}/logo.text.png`
    const win = window.open('', '_blank')
    win.document.write(`<!DOCTYPE html><html><head><title>${name} — Tony's Liquor</title>
      <style>
        body{font-family:sans-serif;margin:0;padding:24px;color:#222}
        h1{font-size:22px;margin:4px 0 2px}
        .sub{color:#555;font-size:13px;margin-bottom:16px}
        table{width:100%;border-collapse:collapse;font-size:13px}
        thead tr{background:#0e947a;color:#fff}
        thead th{padding:9px 10px;text-align:left}
        tfoot td{padding:8px 10px;font-weight:600;border-top:2px solid #0e947a}
        .header{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:8px}
        .logo-img{height:44px;width:auto;object-fit:contain}
        .logo-fallback{color:#0e947a;font-weight:700;font-size:18px;letter-spacing:1px;display:none}
        .gold{color:#d4af37}
        @media print{body{padding:0}}
      </style></head><body>
      <div class="header">
        <img src="${logoUrl}" alt="Tony's Liquor" class="logo-img" onerror="this.style.display='none';document.querySelector('.logo-fallback').style.display='block'" />
        <span class="logo-fallback">TONY'S <span class="gold">LIQUOR</span></span>
        <div style="text-align:right;font-size:12px;color:#888">${dateStr}</div>
      </div>
      <h1>${name}</h1>
      <div class="sub">${desc ? desc + ' · ' : ''}${validRows.length} line${validRows.length !== 1 ? 's' : ''}</div>
      <table>
        <thead><tr><th>Description</th><th style="text-align:center">Qty</th><th>Note</th></tr></thead>
        <tbody>${rowsHtml}</tbody>
        <tfoot><tr><td colspan="3">Total lines: ${validRows.length}</td><td>Total units: ${validRows.reduce((s,r)=>s+(parseInt(r.quantity)||0),0)}</td></tr></tfoot>
      </table>
      <script>window.onload=function(){window.print();window.close()}<\/script>
      </body></html>`)
    win.document.close()
  }

  if (loading || rows === null) return <PageSpinner />

  const validRowCount = rows.filter(r => (r.description || r.stock_code || '').trim()).length
  const totalQty = rows.reduce((s, r) => s + (parseInt(r.quantity) || 0), 0)

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="text-gray-400 hover:text-gray-600 transition-colors">
          <ChevronRight size={20} className="rotate-180" />
        </button>
        <div className="flex-1 min-w-0">
          <input
            className="font-heading text-2xl text-gray-900 dark:text-white bg-transparent border-0 border-b-2 border-transparent focus:border-primary focus:outline-none w-full pb-0.5 transition-colors"
            value={name}
            onChange={e => { setName(e.target.value); setDirty(true) }}
            placeholder="Template name…"
          />
          <input
            className="text-sm text-gray-400 bg-transparent border-0 focus:outline-none w-full mt-1"
            value={desc}
            onChange={e => { setDesc(e.target.value); setDirty(true) }}
            placeholder="Description (optional)…"
          />
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => loadMut.mutate()}
            disabled={loadMut.isLoading || validRowCount === 0}
            className="btn-outline flex items-center gap-1.5 text-sm disabled:opacity-50"
          >
            <ShoppingCart size={14} /> Load to Cart
          </button>
          <button
            onClick={handleExportCSV}
            disabled={validRowCount === 0}
            title="Export as CSV"
            className="btn-outline flex items-center gap-1.5 text-sm disabled:opacity-50"
          >
            <Download size={14} /> CSV
          </button>
          <button
            onClick={handlePrint}
            disabled={validRowCount === 0}
            title="Print / Export PDF"
            className="btn-outline flex items-center gap-1.5 text-sm disabled:opacity-50"
          >
            <Printer size={14} /> PDF
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="btn-primary flex items-center gap-1.5 text-sm"
          >
            <Save size={14} /> {saving ? 'Saving…' : dirty ? 'Save Changes' : 'Saved'}
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div className="flex gap-4 mb-4 text-sm text-gray-500 dark:text-gray-400">
        <span>{validRowCount} line{validRowCount !== 1 ? 's' : ''}</span>
        <span>·</span>
        <span>{totalQty} total units</span>
        {dirty && <span className="text-amber-500 flex items-center gap-1"><AlertCircle size={13} /> Unsaved changes</span>}
        {!dirty && validRowCount > 0 && <span className="text-green-500 flex items-center gap-1"><CheckCircle size={13} /> Saved</span>}
      </div>

      {/* Product Search */}
      <div className="relative mb-4" ref={searchRef}>
        <div className="flex items-center gap-2 input-field py-0 px-3">
          <Search size={15} className="text-gray-400 flex-shrink-0" />
          <input
            className="flex-1 bg-transparent py-2.5 text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none"
            placeholder="Search products by name or stock code to add…"
            value={search}
            onChange={e => handleProductSearch(e.target.value)}
            autoComplete="off"
          />
          {searching && (
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin flex-shrink-0" />
          )}
          {search && !searching && (
            <button onClick={() => { setSearch(''); setSearchRes([]) }} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
              <X size={14} />
            </button>
          )}
        </div>
        {searchRes.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-30 overflow-hidden max-h-60 overflow-y-auto">
            {searchRes.map(p => (
              <button
                key={p.id}
                onClick={() => addProduct(p)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors border-b border-gray-100 dark:border-gray-700/50 last:border-0"
              >
                <Package size={14} className="text-primary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-display text-gray-800 dark:text-gray-100 truncate">{p.description || p.stock_code}</p>
                  <p className="text-xs font-mono text-gray-400">{p.stock_code}</p>
                </div>
                <span className="text-xs font-display font-semibold text-amber-600 dark:text-gold flex-shrink-0">
                  R{(p.inclprice ?? 0).toFixed(2)}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Grid */}
      <div className="card overflow-hidden mb-4">
        {/* Column headers */}
        <div className="grid grid-cols-[3fr_90px_2fr_40px] gap-3 px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700 text-xs font-display font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          <span>Description</span>
          <span>Qty</span>
          <span>Note</span>
          <span></span>
        </div>

        {/* Rows */}
        <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
          {rows.map((row, idx) => (
            <TemplateRow
              key={idx}
              row={row}
              index={idx}
              onUpdate={updateRow}
              onRemove={() => removeRow(idx)}
              canRemove={rows.length > 1}
            />
          ))}
        </div>

        {/* Add row */}
        <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={addRow}
            className="text-sm text-primary hover:text-primary-dark flex items-center gap-1.5 font-display font-medium transition-colors"
          >
            <Plus size={14} /> Add Line
          </button>
        </div>
      </div>

      <p className="text-xs text-gray-400">
        Tip: Search for products by name or stock code above to add them. Quantities and notes are optional. Save when done, then use <strong>Load to Cart</strong> to start an order.
      </p>
    </div>
  )
}

// ── Template Row ───────────────────────────────────────────────────
function TemplateRow({ row, index, onUpdate, onRemove, canRemove }) {
  return (
    <div className="grid grid-cols-[3fr_90px_2fr_40px] gap-3 px-4 py-2.5 items-center hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors group">
      <input
        className="input-field text-sm py-1.5"
        value={row.description || ''}
        onChange={e => onUpdate(index, 'description', e.target.value)}
        placeholder="Product description"
      />
      <input
        type="number"
        min={1}
        className="input-field text-sm py-1.5 text-center"
        value={row.quantity}
        onChange={e => onUpdate(index, 'quantity', e.target.value)}
      />
      <input
        className="input-field text-sm py-1.5"
        value={row.note}
        onChange={e => onUpdate(index, 'note', e.target.value)}
        placeholder="Optional note…"
      />
      <button
        onClick={onRemove}
        disabled={!canRemove}
        className="p-1.5 rounded text-gray-300 hover:text-red-500 disabled:opacity-20 transition-colors mx-auto"
      >
        <X size={14} />
      </button>
    </div>
  )
}
